export type RefreshingCacheOptions = {
  readonly validityWindowMs: number;
  readonly onRefreshFailure?: (error: Error) => void;
  readonly retryAfterFailureMs?: number;
};

/**
 * Holds one loaded value for a validity window, shares a refresh between the
 * callers that arrive during it, and serves the last value it read — or
 * `neverRead` — when a refresh fails.
 */
export function createRefreshingCache<T>(
  load: () => Promise<T>,
  neverRead: () => T,
  options: RefreshingCacheOptions
): () => Promise<T> {
  const onRefreshFailure = options.onRefreshFailure ?? (() => {});
  const retryAfterFailureMs = options.retryAfterFailureMs ?? 0;

  let cached: T | undefined;
  let lastFetchTime: number | undefined;
  let lastFailureTime: number | undefined;
  let inFlightRefresh: Promise<T> | undefined;

  const isFresh = (): boolean => lastFetchTime !== undefined && Date.now() - lastFetchTime < options.validityWindowMs;
  const isHoldingOffAfterFailure = (): boolean =>
    lastFailureTime !== undefined && Date.now() - lastFailureTime < retryAfterFailureMs;

  const refresh = (): Promise<T> => {
    if (inFlightRefresh === undefined) {
      inFlightRefresh = load()
        .then((value) => {
          cached = value;
          lastFetchTime = Date.now();

          return value;
        })
        .finally(() => {
          inFlightRefresh = undefined;
        });
    }

    return inFlightRefresh;
  };

  return async (): Promise<T> => {
    if (cached !== undefined && isFresh()) {
      return cached;
    }
    if (isHoldingOffAfterFailure()) {
      return cached ?? neverRead();
    }

    try {
      return await refresh();
    } catch (error) {
      lastFailureTime = Date.now();
      onRefreshFailure(error as Error);

      return cached ?? neverRead();
    }
  };
}
